import { useEffect, useMemo, useRef, useState, type ComponentProps, type ReactElement } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { AdminAttentionDto } from '@ecom/contracts';
import { useAuthStore } from '@/stores/auth.store';
import { ATTENTION_INTERVAL_MS, useWorkspaceStore } from '@/stores/workspace.store';
import { useSearchStore } from '@/stores/search.store';
import { otherLocale, useUiStore, type AdminLocale } from '@/i18n';
import { translate, type MessageKey } from '@/i18n/messages';
import { useT, useStatusLabel } from '@/i18n/use-t';
import { count, money } from '@/lib/format';
import { applyAccent } from '@/lib/accent';
import { mediaUrl } from '@/lib/media';
import {
  HOME,
  SETTINGS,
  crumbsOf,
  isWithin,
  mayOpen,
  searchablePages,
  sectionOf,
  viewerOf,
  visibleSections,
  type AttentionKey,
  type NavIcon,
  type NavItem,
  type NavSection,
  type Viewer,
} from '@/lib/nav';
import { PageErrorBoundary } from './error-boundary';
import { useDismiss } from './menu';
import { InfinityMark } from './infinity-mark';
import { StoreMark } from './store-mark';
import { Alert, Button, IconButton, Pill, cx } from './ui';
import {
  BellIcon,
  ExternalIcon,
  ArchiveIcon,
  BoltIcon,
  BoxIcon,
  BriefcaseIcon,
  CartIcon,
  ChartIcon,
  ChevronRightIcon,
  ChatIcon,
  ClockIcon,
  CloseIcon,
  CodeIcon,
  CogIcon,
  CoinsIcon,
  FileIcon,
  GridIcon,
  HomeIcon,
  KeyIcon,
  LayersIcon,
  LayoutIcon,
  MailIcon,
  MegaphoneIcon,
  MenuIcon,
  MonitorIcon,
  MoonIcon,
  PaletteIcon,
  PanelIcon,
  PercentIcon,
  PlugIcon,
  QuestionIcon,
  ReceiptIcon,
  ReturnIcon,
  SearchIcon,
  ShieldIcon,
  SignOutIcon,
  SlidersIcon,
  SparkleIcon,
  StarIcon,
  StoreIcon,
  SwatchIcon,
  SunIcon,
  TagIcon,
  TruckIcon,
  UploadIcon,
  UsersIcon,
  WalletIcon,
  GiftIcon,
} from './icons';

/**
 * The admin shell.
 *
 * Exactly the window's height: the sidebar runs top to bottom, and the page
 * scrolls in the area beside it, so the menu never scrolls away with a long
 * list. The sidebar is the daily work (`lib/nav.ts`) — each section a page
 * whose inner pages unfold beneath it while one is open — with Settings at its
 * foot, above who is signed in. It folds to a rail of icons, remembered per
 * browser; on a phone the same menu is a drawer from the top bar.
 *
 * ⌘K (Ctrl K) searches every page someone may open, in either language — a
 * store's admin outgrows a menu long before its operators learn where
 * everything is.
 */

export const ICONS: Record<NavIcon, (props: { className?: string }) => ReactElement> = {
  archive: ArchiveIcon,
  bolt: BoltIcon,
  box: BoxIcon,
  briefcase: BriefcaseIcon,
  cart: CartIcon,
  chart: ChartIcon,
  chat: ChatIcon,
  clock: ClockIcon,
  code: CodeIcon,
  cog: CogIcon,
  coins: CoinsIcon,
  file: FileIcon,
  grid: GridIcon,
  home: HomeIcon,
  key: KeyIcon,
  layers: LayersIcon,
  layout: LayoutIcon,
  mail: MailIcon,
  megaphone: MegaphoneIcon,
  menu: MenuIcon,
  palette: PaletteIcon,
  percent: PercentIcon,
  plug: PlugIcon,
  question: QuestionIcon,
  receipt: ReceiptIcon,
  return: ReturnIcon,
  shield: ShieldIcon,
  sliders: SlidersIcon,
  sparkle: SparkleIcon,
  star: StarIcon,
  store: StoreIcon,
  swatch: SwatchIcon,
  tag: TagIcon,
  truck: TruckIcon,
  upload: UploadIcon,
  users: UsersIcon,
  wallet: WalletIcon,
  gift: GiftIcon,
};

export function Glyph({ name, className }: { name: NavIcon; className: string }) {
  const Icon = ICONS[name];
  return <Icon className={className} />;
}

export function Shell() {
  const user = useAuthStore((state) => state.user);
  const { menuCollapsed, setMenuCollapsed } = useUiStore();
  const { loadWorkspace, refreshAttention } = useWorkspaceStore();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const main = useRef<HTMLElement>(null);
  const t = useT();

  const viewer = useMemo(() => viewerOf(user), [user]);
  const sections = visibleSections(viewer);

  // A new page starts at its top — the scroll area is ours, not the window's,
  // so the browser does not do this for us.
  useEffect(() => {
    main.current?.scrollTo({ top: 0 });
  }, [pathname]);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  // The store's colour, when the owner gave it to the admin (ADR-123). Only once
  // the workspace has answered: until then the remembered accent stands.
  useEffect(() => {
    if (workspace) applyAccent(workspace.accent ?? null);
  }, [workspace]);

  // The tab says whose admin it is.
  useEffect(() => {
    document.title = workspace?.storeName ? `${workspace.storeName} · ${t('appName')}` : t('appName');
  }, [workspace?.storeName, t]);

  // What is waiting: on every page, once a minute, and on coming back to the tab.
  useEffect(() => {
    void refreshAttention();
  }, [pathname, refreshAttention]);
  useEffect(() => {
    const timer = setInterval(() => void refreshAttention(), ATTENTION_INTERVAL_MS);
    const onFocus = () => void refreshAttention();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshAttention]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearching((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex h-dvh overflow-hidden bg-surface-sunken">
      {/* On the inline-start edge, which in Arabic is the right — logical properties only. */}
      <aside
        className={cx(
          'nav-ground hidden h-full shrink-0 flex-col text-nav-ink transition-[width] duration-200 lg:flex',
          menuCollapsed ? 'w-rail' : 'w-sidebar',
        )}
      >
        <Sidebar sections={sections} pathname={pathname} collapsed={menuCollapsed} onCollapse={setMenuCollapsed} />
      </aside>

      {drawerOpen ? <Drawer sections={sections} pathname={pathname} onClose={() => setDrawerOpen(false)} /> : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          viewer={viewer}
          sections={sections}
          pathname={pathname}
          onOpenMenu={() => setDrawerOpen(true)}
          onSearch={() => setSearching(true)}
        />
        {/* `relative`: anything absolute on a page (a visually hidden input) scrolls
            with the page, instead of being placed against the window and stretching it. */}
        <main ref={main} id="main" className="relative min-h-0 flex-1 overflow-y-auto">
          <PageErrorBoundary resetKey={pathname} fallback={(reload) => <PageCrashed onReload={reload} />}>
            <Outlet />
          </PageErrorBoundary>
        </main>
      </div>

      {searching ? <PageSearch viewer={viewer} onClose={() => setSearching(false)} /> : null}
    </div>
  );
}

/** A control on the forest menu: no frame, the menu's own inks. */
function NavButton({ label, onClick, children, className, ...props }: ComponentProps<'button'> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cx(
        'inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-nav-muted transition-colors hover:bg-nav-raised hover:text-nav-ink',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/** The menu itself — the desktop sidebar, folded or not, and the phone's drawer. */
function Sidebar({
  sections,
  pathname,
  collapsed,
  onCollapse,
  onClose,
}: {
  sections: NavSection[];
  pathname: string;
  collapsed: boolean;
  /** The desktop sidebar's fold; absent in the drawer. */
  onCollapse?: (collapsed: boolean) => void;
  /** The drawer's close, also called on the way to a page; absent on desktop. */
  onClose?: () => void;
}) {
  const t = useT();
  const { locale } = useUiStore();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const attention = useWorkspaceStore((state) => state.attention);
  const active = sectionOf(pathname, sections);
  // Whose admin this is: the store's own name and logo (ADR-123), the admin's name until it is known.
  const storeName = workspace?.storeName || t('appName');
  const logo = mediaUrl(workspace?.logoUrl ?? null);

  return (
    <>
      <div className={cx('flex items-center gap-3 pt-5 pb-4', collapsed ? 'flex-col px-2' : 'px-4')}>
        <StoreMark logo={logo} name={storeName} />
        {collapsed ? null : (
          <div className="min-w-0 flex-1">
            <p dir="auto" className="truncate text-start text-[15px] leading-tight font-semibold text-nav-ink">
              {storeName}
            </p>
            {workspace?.storeName ? <p className="mt-0.5 truncate text-xs text-nav-faint">{t('appName')}</p> : null}
          </div>
        )}
        {onCollapse ? (
          <NavButton label={collapsed ? t('expandMenu') : t('collapseMenu')} aria-expanded={!collapsed} onClick={() => onCollapse(!collapsed)}>
            <PanelIcon className="size-[18px] rtl:-scale-x-100" />
          </NavButton>
        ) : null}
        {onClose ? (
          <NavButton label={t('close')} onClick={onClose}>
            <CloseIcon className="size-[18px]" />
          </NavButton>
        ) : null}
      </div>

      <div className={cx('mb-2 h-px bg-nav-line', collapsed ? 'mx-3' : 'mx-4')} />

      <nav
        aria-label={t('appName')}
        className={cx(
          // A fade at the foot, so a menu longer than the window shows there is more below.
          'flex flex-1 flex-col gap-0.5 overflow-y-auto pt-2 pb-6 [mask-image:linear-gradient(to_bottom,black_calc(100%-1.5rem),transparent)] [scrollbar-color:var(--color-nav-line)_transparent]',
          collapsed ? 'items-center px-2' : 'px-3',
        )}
      >
        <Entry item={HOME} locale={locale} collapsed={collapsed} onNavigate={onClose} />
        {sections.map((section) => (
          <Section
            key={section.id}
            section={section}
            locale={locale}
            pathname={pathname}
            collapsed={collapsed}
            current={active === section.id}
            attention={attention}
            onNavigate={onClose}
          />
        ))}
      </nav>

      {/* The platform's quiet signature (ADR-124): at the foot of the menu, outside its
          scroll so it is never cut, nothing to click — and gone on a short window,
          where the entries need the room. */}
      <div aria-hidden className="pointer-events-none flex shrink-0 justify-center pt-3 pb-4 [@media(max-height:46rem)]:hidden">
        <InfinityMark className={collapsed ? 'w-9' : 'w-[5.5rem]'} />
      </div>

      <div className={cx('border-t border-nav-line pt-2', collapsed ? 'flex flex-col items-center px-2' : 'px-3')}>
        <Entry
          item={SETTINGS}
          locale={locale}
          collapsed={collapsed}
          onNavigate={onClose}
          // Failed messages live in the settings area's log; a dot says to look.
          dot={(attention.messagesFailed ?? 0) > 0}
        />
      </div>
      <Account collapsed={collapsed} />
    </>
  );
}

/** The row every top-level entry shares; `on` is the open page, `within` its section. */
function rowClass({ collapsed, on, within }: { collapsed: boolean; on: boolean; within: boolean }): string {
  if (collapsed) {
    return cx(
      'relative inline-flex size-11 shrink-0 items-center justify-center rounded-xl transition-colors',
      on || within ? 'bg-nav-active text-brand-400' : 'text-nav-muted hover:bg-nav-raised hover:text-nav-ink',
    );
  }
  return cx(
    'group relative flex h-10 items-center gap-3 rounded-[0.625rem] px-3 text-[13.5px] transition-colors',
    on
      ? // The open page: a lighter row and a Mint mark on the menu's inline-start edge.
        'bg-nav-active font-semibold text-nav-ink before:absolute before:inset-y-2.5 before:-start-3 before:w-[3px] before:rounded-e-full before:bg-brand-400'
      : within
        ? 'font-semibold text-nav-ink hover:bg-nav-raised'
        : 'text-nav-muted hover:bg-nav-raised hover:text-nav-ink',
  );
}

function iconClass(lit: boolean, collapsed: boolean): string {
  return cx(
    'shrink-0 transition-colors',
    collapsed ? 'size-5' : 'size-[18px]',
    !collapsed && (lit ? 'text-brand-400' : 'text-nav-faint group-hover:text-nav-muted'),
  );
}

/**
 * A section: its own entry, which opens its first page, and — while one of
 * its pages is open — the rest of them beneath it.
 */
function Section({
  section,
  locale,
  pathname,
  collapsed,
  current,
  attention,
  onNavigate,
}: {
  section: NavSection;
  locale: AdminLocale;
  pathname: string;
  collapsed: boolean;
  /** The open page is in this section. */
  current: boolean;
  attention: AdminAttentionDto;
  onNavigate?: () => void;
}) {
  const [first, ...rest] = section.items;
  const name = translate(section.key, locale);
  const onFirst = isWithin(pathname, first.to);
  const own = countOf(attention, first.badge);
  // Something waiting on a page inside that is not on show: the rail, or a section that is closed.
  const inside = rest.some((item) => countOf(attention, item.badge) > 0);

  if (collapsed) {
    return (
      <NavLink
        to={first.to}
        onClick={onNavigate}
        aria-label={own > 0 ? `${name} (${count(own, locale)})` : name}
        title={name}
        className={rowClass({ collapsed, on: onFirst, within: current })}
      >
        <Glyph name={section.icon} className={iconClass(current, collapsed)} />
        {own > 0 || inside ? <Dot className="absolute end-2 top-2" /> : null}
      </NavLink>
    );
  }

  return (
    <div>
      <NavLink
        to={first.to}
        onClick={onNavigate}
        title={translate(section.key, otherLocale(locale))}
        className={rowClass({ collapsed, on: onFirst, within: current })}
      >
        <Glyph name={section.icon} className={iconClass(current, collapsed)} />
        <span className="min-w-0 flex-1 truncate">{name}</span>
        {own > 0 ? <Badge value={own} locale={locale} /> : !current && inside ? <Dot /> : null}
      </NavLink>
      {current && rest.length > 0 ? (
        // A guide line under the section's icon ties its pages to it.
        <ul className="mt-0.5 mb-1.5 ms-[1.3rem] flex flex-col gap-0.5 border-s border-nav-line ps-2.5">
          {rest.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                onClick={onNavigate}
                title={translate(item.key, otherLocale(locale))}
                className={({ isActive }) =>
                  cx(
                    'relative flex h-9 items-center rounded-lg px-3 text-[13px] transition-colors',
                    isActive
                      ? 'bg-nav-active font-medium text-nav-ink before:absolute before:inset-y-2 before:-start-[11px] before:w-[2px] before:rounded-full before:bg-brand-400'
                      : 'text-nav-muted hover:bg-nav-raised hover:text-nav-ink',
                  )
                }
              >
                <span className="min-w-0 flex-1 truncate">{translate(item.key, locale)}</span>
                {countOf(attention, item.badge) > 0 ? (
                  <Badge value={countOf(attention, item.badge)} locale={locale} subtle />
                ) : null}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** A top-level entry that is only a page: Home, Settings. */
function Entry({
  item,
  locale,
  collapsed,
  onNavigate,
  dot = false,
}: {
  item: NavItem;
  locale: AdminLocale;
  collapsed: boolean;
  onNavigate?: () => void;
  /** Something inside wants a look. */
  dot?: boolean;
}) {
  const name = translate(item.key, locale);

  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      aria-label={collapsed ? name : undefined}
      // The other language as the tooltip — the term someone only knows in English is a hover away.
      title={collapsed ? name : translate(item.key, otherLocale(locale))}
      className={({ isActive }) => rowClass({ collapsed, on: isActive, within: false })}
    >
      {({ isActive }) => (
        <>
          <Glyph name={item.icon} className={iconClass(isActive, collapsed)} />
          {collapsed ? null : <span className="min-w-0 flex-1 truncate">{name}</span>}
          {dot ? <Dot className={collapsed ? 'absolute end-2 top-2' : undefined} /> : null}
        </>
      )}
    </NavLink>
  );
}

/** Who is signed in, and the way out. */
function Account({ collapsed }: { collapsed: boolean }) {
  const user = useAuthStore((state) => state.user);
  const signOut = useAuthStore((state) => state.signOut);
  const t = useT();
  if (!user) return null;

  const avatar = (
    <span
      title={collapsed ? `${user.name} · ${user.email}` : undefined}
      className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-400 text-xs font-bold text-brand-900"
    >
      {initials(user.name)}
    </span>
  );
  const out = (
    <NavButton label={t('signOut')} onClick={() => void signOut()}>
      <SignOutIcon className="size-4 rtl:-scale-x-100" />
    </NavButton>
  );

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1 p-2 pb-3">
        <Link to="/security" title={t('securityTitle')} className="rounded-full">
          {avatar}
        </Link>
        {out}
      </div>
    );
  }
  return (
    <div className="p-3">
      <div className="flex items-center gap-1 rounded-xl bg-nav-raised/60 p-1.5 ring-1 ring-nav-line">
        {/* Their own sign-in security (ADR-116) sits behind their own name. */}
        <Link
          to="/security"
          title={t('securityTitle')}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-1 py-0.5 transition-colors hover:bg-nav-raised"
        >
          {avatar}
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-nav-ink">{user.name}</p>
            <p className="truncate text-xs text-nav-faint">{user.email}</p>
          </div>
        </Link>
        {out}
      </div>
    </div>
  );
}

/** The phone's menu: the same sidebar, over the page, gone on the way to a page. */
function Drawer({ sections, pathname, onClose }: { sections: NavSection[]; pathname: string; onClose: () => void }) {
  const t = useT();
  const { theme, cycleTheme } = useUiStore();
  const storefrontUrl = useWorkspaceStore((state) => state.workspace?.storefrontUrl ?? null);
  const ThemeIcon = theme === 'dark' ? MoonIcon : theme === 'light' ? SunIcon : MonitorIcon;
  const themeLabel = theme === 'dark' ? t('themeDark') : theme === 'light' ? t('themeLight') : t('themeSystem');

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <div className="animate-fade absolute inset-0 bg-scrim/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('appName')}
        className="nav-ground animate-drawer absolute inset-y-0 start-0 flex w-sidebar max-w-[85vw] flex-col text-nav-ink shadow-float"
      >
        <Sidebar sections={sections} pathname={pathname} collapsed={false} onClose={onClose} />
        {/* What the phone's top bar has no room for: the shop, and the theme. */}
        <div className="flex items-center gap-1 border-t border-nav-line px-3 py-2">
          {storefrontUrl ? (
            <a
              href={storefrontUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 flex-1 items-center gap-2 rounded-lg px-3 text-[13px] text-nav-muted transition-colors hover:bg-nav-raised hover:text-nav-ink"
            >
              <ExternalIcon className="size-4" />
              {t('viewStore')}
            </a>
          ) : (
            <span className="flex-1" />
          )}
          <NavButton label={themeLabel} onClick={cycleTheme}>
            <ThemeIcon className="size-[18px]" />
          </NavButton>
        </div>
      </aside>
    </div>
  );
}

/** Where the open page sits: its section, then the page. */
function Crumbs({ sections, pathname }: { sections: NavSection[]; pathname: string }) {
  const t = useT();
  const crumbs = crumbsOf(pathname, sections);
  if (crumbs.length === 0) return null;

  return (
    <nav aria-label={t('breadcrumb')} className="hidden min-w-0 lg:block">
      <ol className="flex min-w-0 items-center gap-1.5 text-sm">
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1;
          return (
            <li key={`${crumb.to}:${crumb.key}`} className="flex min-w-0 items-center gap-1.5">
              {index > 0 ? <ChevronRightIcon className="size-3.5 shrink-0 text-ink-faint rtl:-scale-x-100" /> : null}
              {last ? (
                <span aria-current="page" className="flex min-w-0 items-center gap-2 truncate font-semibold text-ink">
                  {index === 0 ? <Glyph name={crumb.icon} className="size-4 shrink-0 text-brand-600" /> : null}
                  {t(crumb.key)}
                </span>
              ) : (
                <Link to={crumb.to} className="flex items-center gap-2 truncate text-ink-muted transition-colors hover:text-ink">
                  <Glyph name={crumb.icon} className="size-4 shrink-0 text-ink-faint" />
                  {t(crumb.key)}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Where you are, the page search, the shop, what is waiting, language, and theme. */
function TopBar({
  viewer,
  sections,
  pathname,
  onOpenMenu,
  onSearch,
}: {
  viewer: Viewer;
  sections: NavSection[];
  pathname: string;
  onOpenMenu: () => void;
  onSearch: () => void;
}) {
  const { locale, setLocale, theme, cycleTheme } = useUiStore();
  const storefrontUrl = useWorkspaceStore((state) => state.workspace?.storefrontUrl ?? null);
  const closed = useWorkspaceStore((state) => state.workspace?.closed ?? false);
  const storeName = useWorkspaceStore((state) => state.workspace?.storeName ?? '');
  const logo = useWorkspaceStore((state) => mediaUrl(state.workspace?.logoUrl ?? null));
  const t = useT();

  const ThemeIcon = theme === 'dark' ? MoonIcon : theme === 'light' ? SunIcon : MonitorIcon;
  const themeLabel = theme === 'dark' ? t('themeDark') : theme === 'light' ? t('themeLight') : t('themeSystem');

  return (
    <header className="flex h-topbar shrink-0 items-center gap-3 border-b border-line bg-surface px-4 lg:px-8">
      <IconButton label={t('openMenu')} variant="ghost" onClick={onOpenMenu} className="-ms-2 lg:hidden">
        <MenuIcon className="size-5" />
      </IconButton>
      <div className="flex min-w-0 items-center gap-2.5 lg:hidden">
        <StoreMark logo={logo} name={storeName || t('appName')} size="sm" />
        <p dir="auto" className="min-w-0 truncate text-sm font-semibold text-ink">
          {storeName || t('appName')}
        </p>
      </div>

      <Crumbs sections={sections} pathname={pathname} />

      <div className="ms-auto flex items-center gap-1.5">
        <button
          type="button"
          onClick={onSearch}
          className="hidden h-10 w-60 items-center gap-2.5 rounded-control border border-line bg-surface-sunken px-3 text-sm text-ink-faint transition-colors hover:border-line-strong hover:text-ink-muted md:flex xl:w-80"
        >
          <SearchIcon className="size-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-start">{t('searchEverything')}</span>
          <kbd dir="ltr" className="rounded-md border border-line bg-surface px-1.5 py-0.5 font-sans text-[10px] font-medium text-ink-faint">
            ⌘K
          </kbd>
        </button>
        <IconButton label={t('searchPages')} variant="ghost" onClick={onSearch} className="md:hidden">
          <SearchIcon className="size-5" />
        </IconButton>

        {storefrontUrl ? (
          // The shop, in a tab of its own: what a change looks like to a shopper.
          <a
            href={storefrontUrl}
            target="_blank"
            rel="noreferrer"
            title={t('viewStore')}
            className="hidden h-10 items-center gap-1.5 rounded-control px-2.5 text-[13px] font-medium text-ink-muted transition-colors hover:bg-ink/[0.05] hover:text-ink sm:inline-flex"
          >
            <ExternalIcon className="size-[18px]" />
            <span className="hidden xl:inline">{t('viewStore')}</span>
            {/* Closed for maintenance (ADR-071): the link still opens it, for staff. */}
            {closed ? <Pill tone="warning">{t('storeClosed')}</Pill> : null}
          </a>
        ) : null}

        <AttentionBell viewer={viewer} />

        <span aria-hidden className="mx-1 hidden h-6 w-px bg-line sm:block" />

        <button
          type="button"
          onClick={() => setLocale(otherLocale(locale))}
          aria-label={t('toggleLanguage')}
          className="inline-flex h-9 items-center rounded-control bg-ink/[0.05] p-0.5 text-xs"
        >
          {/* Both options visible with the active one raised — clearer than a
              single letter that flips meaning. */}
          {(['en', 'ar'] as const).map((option) => (
            <span
              key={option}
              className={cx(
                'rounded-[calc(var(--radius-control)-3px)] px-2.5 py-1 font-semibold transition-colors',
                locale === option ? 'bg-surface text-ink shadow-card' : 'text-ink-muted',
              )}
            >
              {option === 'en' ? 'EN' : 'ع'}
            </span>
          ))}
        </button>

        {/* On a phone the bar keeps room for the store's name; the theme moves to the drawer.
            A wrapper hides it: `hidden` beside the button's own `inline-flex` resolves by stylesheet order. */}
        <span className="hidden sm:contents">
          <IconButton label={themeLabel} variant="ghost" onClick={cycleTheme}>
            <ThemeIcon className="size-5" />
          </IconButton>
        </span>
      </div>
    </header>
  );
}

/**
 * Every page someone may open, found by typing part of its name in either
 * language. Built from the same menu data, so a page the menu hides from
 * staff is not offered here either.
 */
function PageSearch({ viewer, onClose }: { viewer: Viewer; onClose: () => void }) {
  const t = useT();
  const statusLabel = useStatusLabel();
  const locale = useUiStore((state) => state.locale);
  const navigate = useNavigate();
  const { hits, searching, search, clear } = useSearchStore();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);

  const pages = useMemo(() => searchablePages(viewer), [viewer]);
  const matchedPages = useMemo(() => {
    const needle = query.trim().toLowerCase();
    // The same page can sit in two places (a section's own page is also its first item).
    const seen = new Set<string>();
    return pages.filter(({ item }) => {
      if (seen.has(item.to)) return false;
      const hay = `${translate(item.key, 'ar')} ${translate(item.key, 'en')} ${item.to}`.toLowerCase();
      const hit = !needle || hay.includes(needle);
      if (hit) seen.add(item.to);
      return hit;
    });
  }, [pages, query]);

  // Records, searched on the server a moment after typing stops — only where this person may look.
  useEffect(() => {
    const may = {
      orders: viewer.isOwner || viewer.permissions.includes('orders:read'),
      products: viewer.isOwner || viewer.permissions.includes('products:read'),
      customers: viewer.isOwner || viewer.permissions.includes('customers:read'),
    };
    const timer = setTimeout(() => void search(query, may), 250);
    return () => clearTimeout(timer);
  }, [query, search, viewer]);
  useEffect(() => clear, [clear]);

  /** Everything on offer, in the order shown, so the arrow keys walk all of it. */
  const results: SearchResult[] = useMemo(
    () => [
      ...matchedPages.map(({ item, group }) => ({
        key: `page:${item.to}`,
        to: item.to,
        icon: item.icon,
        title: translate(item.key, locale),
        meta: group ? translate(group, locale) : '',
        kind: 'searchKindPages' as MessageKey,
      })),
      ...(query.trim().length >= 2
        ? [
            ...hits.orders.map((order) => ({
              key: `order:${order.id}`,
              to: `/orders/${order.id}`,
              icon: 'cart' as NavIcon,
              title: order.number,
              ltr: true,
              meta: `${order.shippingAddress.fullName} · ${statusLabel(order.status, order.isPickup)} · ${money(order.totals.grandTotal, locale, order.totals.currency)}`,
              kind: 'searchKindOrders' as MessageKey,
            })),
            ...hits.products.map((product) => ({
              key: `product:${product.id}`,
              to: `/products/${product.id}`,
              icon: 'box' as NavIcon,
              title: product.name,
              meta: money(product.price, locale, product.currency),
              kind: 'searchKindProducts' as MessageKey,
            })),
            ...hits.customers.map((customer) => ({
              key: `customer:${customer.id}`,
              to: `/customers/${customer.id}`,
              icon: 'users' as NavIcon,
              title: customer.name || customer.email || customer.phone || '—',
              meta: customer.email ?? customer.phone ?? '',
              kind: 'searchKindCustomers' as MessageKey,
            })),
          ]
        : []),
    ],
    [matchedPages, hits, query, locale, statusLabel],
  );

  useEffect(() => {
    setCursor((at) => Math.min(at, Math.max(results.length - 1, 0)));
  }, [results.length]);

  const open = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <div className="animate-fade fixed inset-0 z-50 flex items-start justify-center bg-scrim/40 p-4 pt-[12vh] backdrop-blur-[3px]" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('searchPages')}
        className="animate-dialog flex max-h-[70vh] w-full max-w-2xl flex-col overflow-hidden rounded-float border border-line bg-surface shadow-float"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-line px-4">
          <SearchIcon className="size-5 shrink-0 text-ink-faint" />
          <input
            autoFocus
            value={query}
            placeholder={t('searchEverything')}
            aria-label={t('searchEverything')}
            onChange={(event) => {
              setQuery(event.target.value);
              setCursor(0);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') onClose();
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setCursor((at) => Math.min(at + 1, results.length - 1));
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                setCursor((at) => Math.max(at - 1, 0));
              }
              if (event.key === 'Enter' && results[cursor]) open(results[cursor].to);
            }}
            className="h-14 min-w-0 flex-1 bg-transparent text-base text-ink outline-none placeholder:text-ink-faint"
          />
        </div>
        <ul className="min-h-0 flex-1 overflow-y-auto p-2" role="listbox" aria-label={t('searchPages')}>
          {results.length === 0 ? (
            <li className="px-3 py-8 text-center text-sm text-ink-muted">
              {searching ? t('loading') : t('searchPagesEmpty')}
            </li>
          ) : (
            results.map((result, index) => (
              <li key={result.key} role="presentation">
                {index === 0 || results[index - 1].kind !== result.kind ? (
                  <p className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-ink-faint">{t(result.kind)}</p>
                ) : null}
                <button
                  type="button"
                  role="option"
                  aria-selected={index === cursor}
                  ref={index === cursor ? (node) => node?.scrollIntoView({ block: 'nearest' }) : undefined}
                  onMouseEnter={() => setCursor(index)}
                  onClick={() => open(result.to)}
                  className={cx(
                    'flex w-full items-center gap-3 rounded-control px-3 py-2.5 text-start text-sm',
                    index === cursor ? 'bg-brand-500/[0.07] text-ink ring-1 ring-brand-500/30' : 'text-ink',
                  )}
                >
                  <Glyph name={result.icon} className="size-[18px] shrink-0 opacity-70" />
                  <span className="min-w-0 flex-1 truncate" dir={result.ltr ? 'ltr' : undefined}>
                    {result.title}
                  </span>
                  {result.meta ? <span className="max-w-[55%] shrink-0 truncate text-xs text-ink-faint">{result.meta}</span> : null}
                </button>
              </li>
            ))
          )}
        </ul>
        <p className="border-t border-line px-4 py-2 text-[11px] text-ink-faint">{t('searchPagesHint')}</p>
      </div>
    </div>
  );
}

/** Two letters from a name, in whichever script it is written. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => [...part][0] ?? '').join('');
}

interface SearchResult {
  key: string;
  to: string;
  icon: NavIcon;
  title: string;
  meta: string;
  /** Which heading it is shown under. */
  kind: MessageKey;
  /** An identifier — an order number — read left to right in either language. */
  ltr?: boolean;
}

/** The count a menu entry wears, or zero. */
function countOf(attention: AdminAttentionDto, key: AttentionKey | undefined): number {
  return key ? (attention[key] ?? 0) : 0;
}

/** A count beside a menu entry: what is waiting there. */
function Badge({ value, locale, subtle = false }: { value: number; locale: AdminLocale; subtle?: boolean }) {
  return (
    <span
      className={cx(
        'inline-flex min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] leading-5 font-semibold',
        // On the forest menu: Mint with forest digits, or quiet for a page's own count.
        subtle ? 'bg-nav-raised text-nav-muted' : 'bg-brand-400 text-brand-900',
      )}
    >
      {value > 99 ? `${count(99, locale)}+` : count(value, locale)}
    </span>
  );
}

/** Something waiting inside, when there is no room for a number. */
function Dot({ className }: { className?: string }) {
  return <span aria-hidden className={cx('inline-block size-2 shrink-0 rounded-full bg-brand-400', className)} />;
}

/** What the top bar's bell lists: each count, its words, and the page that handles it. */
const ATTENTION_ITEMS: { key: AttentionKey; label: MessageKey; to: string; permission: string }[] = [
  { key: 'ordersToFulfil', label: 'attentionOrdersToFulfil', to: '/orders?view=toFulfil', permission: 'orders:read' },
  { key: 'ordersUnpaid', label: 'attentionOrdersUnpaid', to: '/orders?view=unpaid', permission: 'orders:read' },
  { key: 'returnsToHandle', label: 'attentionReturns', to: '/returns', permission: 'returns:read' },
  { key: 'ticketsOpen', label: 'attentionTickets', to: '/support', permission: 'customers:read' },
  { key: 'reviewsPending', label: 'attentionReviews', to: '/reviews', permission: 'reviews:moderate' },
  { key: 'questionsPending', label: 'attentionQuestions', to: '/questions', permission: 'reviews:moderate' },
  { key: 'tradeApplicationsPending', label: 'attentionTrade', to: '/trade-applications', permission: 'customers:read' },
  { key: 'followUpsDue', label: 'attentionFollowUps', to: '/follow-ups', permission: 'customers:read' },
  { key: 'lowStock', label: 'attentionLowStock', to: '/inventory', permission: 'inventory:manage' },
  { key: 'messagesFailed', label: 'attentionMessagesFailed', to: '/settings/messages', permission: 'customers:read' },
];

/**
 * What is waiting on the team, in one list (docs/07-Admin/Admin-App.md) —
 * the badges gathered, for whoever opens the admin wondering where to start.
 * Each line opens the page that handles it.
 */
function AttentionBell({ viewer }: { viewer: Viewer }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const attention = useWorkspaceStore((state) => state.attention);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useDismiss(open, root, () => setOpen(false));

  const items = ATTENTION_ITEMS.filter(
    (item) =>
      (attention[item.key] ?? 0) > 0 && mayOpen({ to: item.to, key: item.label, icon: 'bolt', permission: item.permission }, viewer),
  );
  // Low stock is a standing condition, not a task — it is listed but does not light the bell.
  const urgent = items.filter((item) => item.key !== 'lowStock').reduce((sum, item) => sum + (attention[item.key] ?? 0), 0);

  return (
    <div ref={root} className="relative">
      <IconButton
        label={urgent > 0 ? `${t('needsAttention')} (${count(urgent, locale)})` : t('needsAttention')}
        variant="ghost"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="relative"
      >
        <BellIcon className="size-5" />
        {urgent > 0 ? (
          <span className="absolute end-0.5 top-0.5 inline-flex min-w-[1.125rem] items-center justify-center rounded-full bg-danger px-1 text-[10px] leading-[1.125rem] font-bold text-danger-ink ring-2 ring-surface">
            {urgent > 99 ? `${count(99, locale)}+` : count(urgent, locale)}
          </span>
        ) : null}
      </IconButton>
      {open ? (
        <div
          role="dialog"
          aria-label={t('needsAttention')}
          className="animate-pop absolute end-0 top-full z-40 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-panel border border-line bg-surface shadow-float"
        >
          <p className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">{t('needsAttention')}</p>
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-ink-muted">{t('allCaughtUp')}</p>
          ) : (
            <ul className="flex flex-col p-1.5">
              {items.map((item) => (
                <li key={item.key}>
                  <Link
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between gap-3 rounded-control px-3 py-2.5 text-sm text-ink transition-colors hover:bg-surface-sunken"
                  >
                    <span className="min-w-0 truncate">{t(item.label)}</span>
                    <span
                      className={cx(
                        'inline-flex min-w-6 items-center justify-center rounded-full px-1.5 text-xs leading-6 font-semibold',
                        item.key === 'lowStock' ? 'bg-ink/[0.06] text-ink-muted' : 'bg-brand-600 text-brand-ink',
                      )}
                    >
                      {count(attention[item.key] ?? 0, locale)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** What a page that failed to draw shows instead, inside a shell that still works. */
function PageCrashed({ onReload }: { onReload: () => void }) {
  const t = useT();
  return (
    <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 py-16">
      <h1 className="text-xl font-bold text-ink">{t('crashTitle')}</h1>
      <Alert>{t('crashBody')}</Alert>
      <div>
        <Button onClick={onReload}>{t('reloadPage')}</Button>
      </div>
    </div>
  );
}
